import Notify from 'quasar/src/plugins/Notify.js';;
import { PDFDocument } from "pdf-lib";
import * as pdfjsLib from "pdfjs-dist";
import i18n from "@/i18n";
import store from "@/store";
import { getFileBlobWithCache } from "@/store/services/order/uploadedDocumentCache";

// Configurer le worker PDF.js
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

interface FileOptions {
    mimeType: string,
    base64: string,
    name: string
}

/**
 * Sets the isLoading property of the row object to true and dispatches an action to download a file.
 * @param {any} row - The row object representing a document.
 */
export const viewDocument = (row: any) => {
    row.isLoading = true
    store.dispatch('taskModule/uploadDocumentModule/downloadFile', {
        request: row.document,
        row: row,
        callback: viewDocumentResponse
    })
}
export const viewDocumentUploadPanel = async (file: any) => {
    const size=getBase64FileSize(file.content)
    if(size>1000){
     
        await openFullPdfInNativeViewer(file.content, file.originalFileName);

    }else{
        openFileInNewTab({ name: file.originalFileName, mimeType: file.format, base64: file.content });
    }

}
export const viewDocumentOrder = (row: any) => {
    row.isLoading = true
    store.dispatch('taskModule/uploadDocumentModule/downloadFile', {
        request: row.documentUpload || {resourceUid: row?.file_resourceuid},
        row: row,
        callback: viewDocumentOrderResponse
    })
    row.isLoading = false
}
export const viewDocumentScannedOrder = (row: any) => {
    store.dispatch('taskModule/uploadDocumentModule/downloadFile', {
        request: row,
        row: row,
        callback: viewDocumentScannedOrderResponse
    })
}
export const viewDocumentParty = (row: any) => {
    row.isLoading = true
    store.dispatch('taskModule/uploadDocumentModule/downloadFile', {
        request: row,
        row: row,
        callback: viewDocumentResponse
    })
}
export const viewDocumentStreamOrder = async (row: any) => {
    const target = row?.documentUpload || row || {};
    const fileResourceUid = target?.resourceUid || row?.file_resourceuid;
    const originalFileName = target?.originalFileName || row?.originalFileName || "document";

    if (!fileResourceUid) {
        Notify.create({
            message: "Aucun identifiant de fichier trouvé",
            color: "negative",
        });
        return;
    }

    if (target) {
        target.isLoading = true;
    }

    try {
        Notify.create({
            message: "Chargement du document...",
            color: "positive",
            timeout: 20000,
        });

        const blob = await getFileBlobWithCache(fileResourceUid, originalFileName);
        if (!blob) {
            throw new Error("Failed to get file blob");
        }

        openBlobInNewTab(blob, originalFileName, row?.documentType);
    } catch (error) {
        console.error("Error loading document stream:", error);
        Notify.create({
            message: "Erreur lors du chargement du document",
            color: "negative",
        });
    } finally {
        if (target) {
            target.isLoading = false;
        }
    }
}


export const printDocumentResonse =(documentUploadResponse: any) => {
    if (documentUploadResponse) {
      const {content, format} = documentUploadResponse;
      const byteCharacters = atob(content);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], {type: format});

            const blobUrl = URL.createObjectURL(blob);

            // Crée dynamiquement un iframe
            const iframe = document.createElement('iframe');
            iframe.style.display = 'none';
            document.body.appendChild(iframe);

            iframe.src = blobUrl;

            iframe.onload = function() {
                iframe?.contentWindow?.focus();
                iframe.contentWindow?.print();

            };
        }
  }
/**
 * Handles the response after downloading the file and opens it in a new tab if successful.
 * @param {any} response - The response object containing the file information.
 * @param {any} row - The row object representing a document.
 */
const viewDocumentResponse = async (response: any, row: any) => {
    if (response) {
        const { format, content } = response
        openFileInNewTab({ name: response.originalFileName, mimeType: format, base64: content }, row.documentType);
        if (row)
            row.isLoading = false;
    }
}
/**
 * Handles the response after downloading the file and opens it in a new tab if successful.
 * @param {any} response - The response object containing the file information.
 * @param {any} row - The row object representing a document.
 */
const viewDocumentOrderResponse = async (response: any, row: any) => {
  if (!response) {
    return;
  }

  const { format, content } = response;

  const pdfFile = await updatePdfMetadata(content, row.documentType);

  const byteCharacters = atob(pdfFile);

  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }

  const byteArray = new Uint8Array(byteNumbers);

  const blob = new Blob([byteArray], { type: format });

  const blobUrl = URL.createObjectURL(blob);

  window.open(blobUrl, "_blank");
  row.isLoading = false;
};

export async function openFullPdfInNativeViewer(base64: string, name?: any, documentType?: string) {
    try {
      const pdfBytes = base64ToUint8Array(base64);
      const title = documentType ? documentType.replace(/_/g, ' ') : name;

      const pdfJsDoc = await pdfjsLib.getDocument({ data: pdfBytes, password: "" }).promise;

      const finalPdfBytes = await renderPdfPagesToImages(pdfJsDoc, title);

      const blob = new Blob([finalPdfBytes], { type: 'application/pdf' });
      const blobUrl = URL.createObjectURL(blob);

      if (!documentWindow.current || documentWindow.current.closed) {
        documentWindow.current = window.open("");
        if (!documentWindow.current) {
          console.error("Impossible d'ouvrir une nouvelle fenêtre.");
          return;
        }
      }
      const win = documentWindow.current;
      win.document.title = documentType || name;
      win.document.open();
      win.document.write(`
        <html>
          <head>
            <title>${win.document.title}</title>
            <style>
              html, body {
                margin: 0;
                height: 100%;
                overflow: hidden;
              }
              iframe {
                width: 100%;
                height: 100%;
                border: none;
              }
            </style>
          </head>
          <body>
            <iframe src="${blobUrl}#toolbar=1&navpanes=1&scrollbar=1"></iframe>
          </body>
        </html>
      `);
      win.document.close();
      win.focus();

    } catch (error) {
      console.error("Erreur lors de l'ouverture du PDF :", error);
    }
  }

const viewDocumentScannedOrderResponse = async (response: any, row: any) => {
    if (response) {
        const { format, content } = response
        const size=getBase64FileSize(content)
        if(size>1000){
            await openFullPdfInNativeViewer(content, response.originalFileName, row.documentType);
        }else{
            openFileInNewTab({ name: response.originalFileName, mimeType: format, base64: content }, row.documentType);
            if (row)
                row.isLoading = false;
        }

   
    }

}
function getBase64FileSize(base64String: any) {

    const cleanedBase64 = base64String.split(',').pop() || '';
 
    const padding = (cleanedBase64.match(/=+$/) || [''])[0].length;
    const sizeInBytes = (cleanedBase64.length * 3) / 4 - padding;

    return (sizeInBytes / 1024); 
}

export const documentWindow: { current: Window | null } = { current: null };

export const openBlobInNewTab = (blob: Blob, name: string, documentType?: string) => {
    const title = (documentType ? documentType.replace(/_/g, " ") : name) || "document";

    const openWithTitle = (url: string) => {
        const win = window.open("", "_blank");
        if (!win) {
            return;
        }

        win.document.open();
        win.document.write(`
          <html>
            <head>
              <title>${title}</title>
              <style>
                html, body {
                  margin: 0;
                  height: 100%;
                  overflow: hidden;
                }
                iframe {
                  width: 100%;
                  height: 100%;
                  border: none;
                }
              </style>
            </head>
            <body>
              <iframe src="${url}#toolbar=1&navpanes=1&scrollbar=1"></iframe>
            </body>
          </html>
        `);
        win.document.close();
        win.document.title = title;
    };

    // For PDF files, set metadata title so PDF viewers can display it in their own header too.
    if (blob.type === "application/pdf") {
        blob.arrayBuffer()
            .then(async (buffer) => {
                try {
                    const pdfDoc = await PDFDocument.load(buffer, { ignoreEncryption: true });
                    // pdf-lib ne sait pas re-chiffrer : réécrire un PDF chiffré (ex. avis
                    // de situation INSEE) produit un fichier illisible par le lecteur natif.
                    // On l'ouvre donc tel quel, sans titre injecté.
                    if (pdfDoc.isEncrypted) {
                        openWithTitle(URL.createObjectURL(blob));
                        return;
                    }
                    pdfDoc.setTitle(title);
                    const updatedPdfBytes = await pdfDoc.save();
                    const updatedBlob = new Blob([updatedPdfBytes], { type: "application/pdf" });
                    const blobUrl = URL.createObjectURL(updatedBlob);
                    openWithTitle(blobUrl);
                } catch (pdfError) {
                    console.error("Erreur metadata PDF, fallback document brut:", pdfError);
                    const blobUrl = URL.createObjectURL(blob);
                    openWithTitle(blobUrl);
                }
            })
            .catch((error) => {
                console.error("Erreur lecture blob PDF:", error);
                const blobUrl = URL.createObjectURL(blob);
                openWithTitle(blobUrl);
            });
        return;
    }

    try {
        const blobUrl = URL.createObjectURL(blob);
        openWithTitle(blobUrl);
    } catch (error) {
        console.error("Erreur lors de l'ouverture du document :", error);
    }
}

/**
 * Opens the file in a new browser tab.
 * @param {Object} fileOptions - The file options containing the MIME type, base64 content, and name of the file.
 */
export const openFileInNewTab = async (fileOptions: FileOptions, documentType?: string) => {
    const {
        mimeType,
        base64,
        name
    } = fileOptions

    let updatedBase64 = base64;
    if (mimeType === 'application/pdf') {
        const title = documentType ? documentType.replace(/_/g, ' ') : name;
        updatedBase64 = await updatePdfMetadata(base64, title);
    }

    const linkSource = `data:${mimeType};base64,${updatedBase64}`;
    //const newWindow = window.open("");
    if (!documentWindow.current || documentWindow.current.closed) {
      documentWindow.current = window.open("");
    }
     if (documentWindow.current) {
    // Met à jour le contenu de la fenêtre
    documentWindow.current.focus();
    documentWindow.current.document.body.innerHTML = `<object type='${mimeType}' width='100%' height='100%' data='${linkSource}'></object>`;
    if(documentType !== undefined) {
        let _documentType = documentType.replace(/_/g, ' ');
      documentWindow.current.document.title = `${_documentType}`;
    } else {
      documentWindow.current.document.title = name;
    }
    Notify.create({
        message: i18n.global.t('order.messages.fileLoadedSuccess'),
        color: 'positive'
      });
    }
}

async function updatePdfMetadata(base64: string, title: string): Promise<string> {
    try {
         const pdfBytes = base64ToUint8Array(base64);
         const pdfJsDoc = await pdfjsLib.getDocument({ data: pdfBytes, password: "" }).promise;
         const renderedPdfBytes = await renderPdfPagesToImages(pdfJsDoc, title);
         return uint8ArrayToBase64(renderedPdfBytes);

    } catch (error) {
        console.error("Failed to update PDF metadata:", error);
        return base64; // Retourne le PDF original en cas d'erreur
    }
}
function base64ToUint8Array(base64: string): Uint8Array {
    const binaryString = atob(base64);
    const length = binaryString.length;
    const bytes = new Uint8Array(length);
    for (let i = 0; i < length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
}

function uint8ArrayToBase64(bytes: Uint8Array): string {
    let binary = '';
    const length = bytes.byteLength;
    for (let i = 0; i < length; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
}

/**
 * Rend chaque page d'un PDF en image PNG et crée un nouveau PDF
 * Utilisé pour décrypter les PDFs cryptés en les rendant visuellement
 */
async function renderPdfPagesToImages(pdfJsDoc: any, title: string): Promise<Uint8Array> {
    const pdfDoc = await PDFDocument.create();
    const renderDpi = 150;
    const scale = Math.max(1, renderDpi / 72);

    for (let p = 1; p <= pdfJsDoc.numPages; p++) {
        try {
            const page = await pdfJsDoc.getPage(p);
            const viewport = page.getViewport({ scale });

            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            if (!ctx) throw new Error('Could not get canvas context');
            canvas.width = Math.ceil(viewport.width);
            canvas.height = Math.ceil(viewport.height);

            ctx.fillStyle = 'white';
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            const renderContext = {
                canvasContext: ctx,
                viewport: viewport,
            };
            await page.render(renderContext).promise;

            const dataUrl = canvas.toDataURL('image/png');

            const base64Data = dataUrl.split(',')[1];
            const binaryString = atob(base64Data);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
            }

            const embeddedImg = await pdfDoc.embedPng(bytes);
            const imgPage = pdfDoc.addPage([viewport.width * 72 / renderDpi, viewport.height * 72 / renderDpi]);
            imgPage.drawImage(embeddedImg, {
                x: 0,
                y: 0,
                width: imgPage.getWidth(),
                height: imgPage.getHeight(),
            });
        } catch (pageError) {
            console.error(`  Error rendering page ${p}:`, pageError);
            throw pageError;
        }
    }

    pdfDoc.setTitle(title);
    const result = await pdfDoc.save();
    return result;
}

export type OptionsObject = {
  label: string;
  id: string;
};

function normalize(s: any) {
  return String(s ?? '')
    .toLowerCase()
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function makeAcronym(s: any) {
  return normalize(s)
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0))
    .join('');
}

function wordsOf(label: string) {
  return label.split(/\W+/).filter(Boolean);
}

/**
 * Generic search filter for lists of objects which have `label` and/or `id`.
 * - returns original list when `query` is empty
 * - prioritizes word-start matches, then acronym matches, then long-substring matches, then id matches
 */
export function searchFilterList(list: OptionsObject[], query: string): OptionsObject[] {
  const arr = Array.isArray(list) ? list : [];
  const needleRaw = normalize(query);
  if (!needleRaw) {
    return arr;
  }

  const tokens = needleRaw.split(/\s+/).filter(Boolean);
  const needleNoSpaces = needleRaw.replace(/\s+/g, '');

  // 1) word-start matches (highest priority)
  const startMatches = arr.filter((item) => {
    const label = normalize(item?.label);
    const id = normalize(item?.id);
    const words = wordsOf(label);
    return tokens.every((t) => words.some((w) => w.startsWith(t)) || id.startsWith(t));
  });
  if (startMatches.length > 0) return startMatches;

  // 2) acronym matches (e.g., "AC" -> "Air Conditioning")
  const acronymMatches = arr.filter((item) => {
    const acronym = makeAcronym(item?.label);
    return needleNoSpaces.length >= 2 && acronym.startsWith(needleNoSpaces);
  });
  if (acronymMatches.length > 0) return acronymMatches;

  // 3) substring/includes allowed only for longer tokens (>3 chars)
  const canUseIncludes = tokens.some((t) => t.length > 3);
  if (canUseIncludes) {
    const includeMatches = arr.filter((item) => {
      const label = normalize(item?.label);
      const id = normalize(item?.id);
      return tokens.every((t) => label.includes(t) || id.includes(t));
    });
    if (includeMatches.length > 0) return includeMatches;
  }

  // 4) id-match fallback (numbers or alphanumeric ids)
  const idMatches = arr.filter((item) => {
    const id = normalize(item?.id);
    return id.includes(needleNoSpaces);
  });
  if (idMatches.length > 0) return idMatches;

  // Nothing matched
  return [];
}